// Globals
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { createRedirect, deleteRedirect, getRedirects, updateRedirect } from '../../feature';

// Components
import { PlusIcon } from '@/icons';
import { Button, Drawer } from '@/ui';
import { RedirectForm } from '../../components';
import RedirectsList from '../../components/RedirectsList/RedirectsList';
import { StyledContentBlock, StyledHeader, StyledPageTitle, StyledPageWrapper } from '../../components/Styled';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IRedirect } from '@/models/redirects.model';

// Helpers
import { useModal, usePagination } from '@/hooks';
import { CreateRedirectDTO, GetRedirectsDTO } from '../../dtos';
import { TRedirectFormValues } from '../../helpers/types';
import { useRedirects } from '../../hooks';

const RedirectsListContainer = () => {
  const { t } = useTranslation('redirects');
  const { canManage } = usePermissions();
  const canManageSeo = canManage(Resource.SEO);
  const { currentPage, onPageChange } = usePagination();
  const { isLoading, redirects, pagination, query } = useRedirects();
  const dispatch = useAppDispatch();
  const [editableRedirect, setEditableRedirect] = useState<IRedirect | null>(null);
  const {
    isOpened: isCreateRedirectOpened,
    openModal: openCreateRedirectModal,
    closeModal: closeCreateRedirectModal,
  } = useModal();

  const handleCreateRedirect = async (data: TRedirectFormValues) => {
    try {
      const dto = new CreateRedirectDTO(data);
      await dispatch(createRedirect(dto)).unwrap();
      await dispatch(getRedirects({ page: 1 }));
      closeCreateRedirectModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (redirect: IRedirect) => {
    try {
      await dispatch(
        deleteRedirect({
          id: redirect.id,
        }),
      ).unwrap();
      await dispatch(getRedirects({ page: 1 }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = async (redirect: IRedirect) => {
    if (!editableRedirect) return;
    try {
      const dto = new CreateRedirectDTO(redirect);
      await dispatch(updateRedirect({ id: editableRedirect.id, ...dto })).unwrap();
      await dispatch(getRedirects({ page: currentPage }));
      setEditableRedirect(null);
      closeCreateRedirectModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangePage = (page: number) => {
    const dto = new GetRedirectsDTO({ page, ...query });
    dispatch(getRedirects(dto));
    onPageChange(page);
  };

  const handleClose = () => {
    setEditableRedirect(null);
    closeCreateRedirectModal();
  };

  return (
    <StyledPageWrapper>
      <StyledHeader>
        <StyledPageTitle>{t('redirect_tab_title')}</StyledPageTitle>
        {canManageSeo && (
          <Button icon={<PlusIcon />} onClick={openCreateRedirectModal}>
            {t('redirect_add')}
          </Button>
        )}
      </StyledHeader>
      <StyledContentBlock>
        <RedirectsList
          isLoading={isLoading}
          currentPage={currentPage}
          onPageChange={handleChangePage}
          total={pagination?.total ?? 0}
          list={redirects ?? []}
          onEdit={setEditableRedirect}
          onDelete={handleDelete}
        />
      </StyledContentBlock>
      <Drawer
        open={isCreateRedirectOpened || !!editableRedirect}
        onClose={handleClose}
        destroyOnClose
        width={700}
        title={t(editableRedirect ? 'redirect_edit' : 'redirect_add')}
      >
        <RedirectForm
          handleSubmit={editableRedirect ? handleEdit : handleCreateRedirect}
          isLoading={isLoading}
          initialValues={editableRedirect}
        />
      </Drawer>
    </StyledPageWrapper>
  );
};

export default RedirectsListContainer;
